import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Buildings, SignIn, SignOut, ArrowLeft, CheckCircle, WarningCircle, ShieldCheck, Camera, MapPin } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = { CHOOSE_ACTION: 0, SELECT_WORKER: 1, CREATE_PIN: 2, ENTER_PIN: 3, PHOTO: 4, SUCCESS: 5 };

const AttendanceMarkPage = () => {
  const { qrToken } = useParams();
  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(STEPS.CHOOSE_ACTION);
  const [actionType, setActionType] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [successHora, setSuccessHora] = useState('');
  const [successDistance, setSuccessDistance] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [geoPos, setGeoPos] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const fileInputRef = useRef(null);

  const loadBranch = useCallback(async (retries = 3) => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/api/asistencia/sucursal/${qrToken}`, { timeout: 10000 });
      setBranchData(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setError('QR invalido o sucursal no encontrada');
      } else if (retries > 0) {
        // Network error or server waking up - retry
        await new Promise(r => setTimeout(r, 2000));
        return loadBranch(retries - 1);
      } else {
        setError('No se pudo conectar al servidor. Intente nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  }, [qrToken]);

  useEffect(() => { loadBranch(); }, [loadBranch]);

  const branchHasGeo = branchData?.latitude != null && branchData?.longitude != null;

  const requestLocation = () => {
    if (!branchHasGeo) { return Promise.resolve(null); }
    setGeoLoading(true);
    setGeoError('');
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setGeoError('Geolocalizacion no soportada en este navegador');
        setGeoLoading(false);
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
          setGeoPos(coords);
          setGeoLoading(false);
          resolve(coords);
        },
        (err) => {
          setGeoError('No se pudo obtener la ubicacion. Active el GPS y permita acceso.');
          setGeoLoading(false);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleChooseAction = (tipo) => {
    setActionType(tipo);
    setSelectedWorker(null);
    setPin('');
    setPinConfirm('');
    setError('');
    setPhotoData(null);
    setGeoPos(null);
    setGeoError('');
    setStep(STEPS.SELECT_WORKER);
  };

  const handleSelectWorker = (workerId) => {
    if (!workerId) { setSelectedWorker(null); return; }
    const worker = branchData?.workers?.find(w => w.id === workerId);
    if (!worker) return;
    setSelectedWorker(worker);
    setPin('');
    setPinConfirm('');
    setError('');
  };

  const handleContinueToPin = () => {
    if (!selectedWorker) { setError('Seleccione un trabajador'); return; }
    setError('');
    if (!selectedWorker.has_pin) {
      setStep(STEPS.CREATE_PIN);
    } else {
      setStep(STEPS.ENTER_PIN);
    }
  };

  const handleCreatePin = async () => {
    setError('');
    if (!pin || pin.length !== 4 || !/^\d{4}$/.test(pin)) { setError('El PIN debe ser de 4 digitos numericos'); return; }
    if (pin !== pinConfirm) { setError('Los PINs no coinciden'); return; }

    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/asistencia/crear-pin`, {
        worker_id: selectedWorker.id,
        code: selectedWorker.code,
        pin,
        pin_confirm: pinConfirm,
      });
      selectedWorker.has_pin = true;
      setStep(STEPS.PHOTO);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear PIN');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyThenPhoto = () => {
    setError('');
    if (!pin || pin.length !== 4) { setError('Ingrese su PIN de 4 digitos'); return; }
    setStep(STEPS.PHOTO);
  };

  const handleFileCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoData(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitMark = async () => {
    setError('');
    setActionLoading(true);

    // Get location if branch has geo
    let coords = geoPos;
    if (branchHasGeo && !coords) {
      coords = await requestLocation();
      if (!coords) {
        setError('Se requiere ubicacion para marcar. Active el GPS y permita acceso.');
        setActionLoading(false);
        return;
      }
    }

    try {
      const res = await axios.post(`${API_URL}/api/asistencia/marcar`, {
        worker_id: selectedWorker.id,
        code: selectedWorker.code,
        pin,
        tipo: actionType,
        qr_token: qrToken,
        latitude: coords?.latitude || null,
        longitude: coords?.longitude || null,
        photo_base64: photoData || null,
      });
      setSuccessMsg(res.data.message);
      setSuccessHora(res.data.hora);
      setSuccessDistance(res.data.distance_meters);
      setStep(STEPS.SUCCESS);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset = () => {
    setActionType(null);
    setSelectedWorker(null);
    setPin('');
    setPinConfirm('');
    setError('');
    setSuccessMsg('');
    setSuccessHora('');
    setSuccessDistance(null);
    setPhotoData(null);
    setGeoPos(null);
    setGeoError('');
    setStep(STEPS.CHOOSE_ACTION);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-pulse text-zinc-400 text-sm">Cargando...</div>
      </div>
    );
  }

  if (error && !branchData) {
    const isNetworkError = error.includes('conectar');
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="bg-white border border-zinc-200 p-8 text-center max-w-sm w-full space-y-4">
          <WarningCircle size={48} className={isNetworkError ? 'text-amber-400 mx-auto' : 'text-red-400 mx-auto'} />
          <h2 className="text-lg font-semibold text-zinc-900">{isNetworkError ? 'Servidor no disponible' : 'QR no valido'}</h2>
          <p className="text-sm text-zinc-500">{error}</p>
          <button
            onClick={() => loadBranch()}
            className="px-6 py-2.5 text-sm font-semibold bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
            data-testid="retry-btn"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const isEntrada = actionType === 'entrada';

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="attendance-mark-page">
      {/* Header */}
      <div className="bg-zinc-900 text-white px-4 py-6 text-center">
        <h1 className="text-2xl font-bold tracking-tight mb-1.5">Control de Asistencia</h1>
        <div className="flex items-center justify-center gap-2">
          <Buildings size={18} weight="bold" className="text-zinc-400" />
          <span className="text-base text-zinc-300">{branchData?.branch_name}</span>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">

        {/* Step 0: Choose Action */}
        {step === STEPS.CHOOSE_ACTION && (
          <div className="flex flex-col items-center gap-6 pt-4" data-testid="step-choose-action">
            <button
              onClick={() => handleChooseAction('entrada')}
              className="w-36 h-36 rounded-full flex flex-col items-center justify-center gap-2 border-[3px] border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-500 active:scale-95 transition-all shadow-sm"
              data-testid="action-entrada-btn"
            >
              <SignIn size={36} weight="bold" />
              <span className="text-sm font-bold tracking-wide">Entrada</span>
            </button>
            <div className="w-12 border-t border-zinc-200"></div>
            <button
              onClick={() => handleChooseAction('salida')}
              className="w-36 h-36 rounded-full flex flex-col items-center justify-center gap-2 border-[3px] border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-500 active:scale-95 transition-all shadow-sm"
              data-testid="action-salida-btn"
            >
              <SignOut size={36} weight="bold" />
              <span className="text-sm font-bold tracking-wide">Salida</span>
            </button>
          </div>
        )}

        {/* Step 1: Select Worker */}
        {step === STEPS.SELECT_WORKER && (
          <div className="space-y-4" data-testid="step-select-worker">
            <button onClick={handleReset} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800" data-testid="back-btn">
              <ArrowLeft size={14} /> Volver
            </button>
            <div className="text-center">
              <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-bold border-2 rounded-full ${
                isEntrada ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-rose-300 bg-rose-50 text-rose-700'
              }`}>
                {isEntrada ? <SignIn size={18} weight="bold" /> : <SignOut size={18} weight="bold" />}
                Marcar {isEntrada ? 'Entrada' : 'Salida'}
              </span>
            </div>
            <div className="bg-white border border-zinc-200 p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 block mb-2">Seleccione trabajador</label>
                <select
                  value={selectedWorker?.id || ''}
                  onChange={(e) => handleSelectWorker(e.target.value)}
                  className="w-full px-3 py-3 text-sm border border-zinc-200 bg-white focus:outline-none focus:border-zinc-400 appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2371717a' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                  data-testid="worker-select"
                >
                  <option value="">-- Seleccionar --</option>
                  {(branchData?.workers || []).map(w => (
                    <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
                  ))}
                </select>
              </div>
              {selectedWorker && (
                <div className="border border-zinc-100 bg-zinc-50 px-4 py-3 space-y-1" data-testid="worker-info-card">
                  <div className="text-sm font-semibold text-zinc-900">{selectedWorker.name}</div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-500">{selectedWorker.cargo}</span>
                    <span className="font-mono text-xs px-2 py-0.5 bg-zinc-200 text-zinc-700 font-bold">{selectedWorker.code}</span>
                  </div>
                </div>
              )}
              {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="error-msg">{error}</p>}
              <button
                onClick={handleContinueToPin}
                disabled={!selectedWorker}
                className={`w-full py-3 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isEntrada ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
                data-testid="continue-pin-btn"
              >
                Continuar
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Create PIN */}
        {step === STEPS.CREATE_PIN && selectedWorker && (
          <div className="space-y-4" data-testid="step-create-pin">
            <button onClick={() => setStep(STEPS.SELECT_WORKER)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800" data-testid="back-btn">
              <ArrowLeft size={14} /> Volver
            </button>
            <div className="bg-white border border-zinc-200 p-5 space-y-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-amber-50 mx-auto flex items-center justify-center mb-2 border border-amber-200 rounded-full">
                  <ShieldCheck size={24} className="text-amber-600" />
                </div>
                <h2 className="text-base font-semibold text-zinc-900">Crear PIN de acceso</h2>
                <p className="text-xs text-zinc-500 mt-1">{selectedWorker.name}</p>
                <span className="font-mono text-xs px-2 py-0.5 bg-zinc-200 text-zinc-700 font-bold inline-block mt-1">{selectedWorker.code}</span>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600 block mb-1">Crear PIN (4 digitos)</label>
                <input type="password" inputMode="numeric" maxLength={4} value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="****" className="w-full px-3 py-3 text-center text-xl tracking-[0.5em] border border-zinc-200 font-mono focus:outline-none focus:border-zinc-400"
                  data-testid="pin-input" autoFocus />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600 block mb-1">Confirmar PIN</label>
                <input type="password" inputMode="numeric" maxLength={4} value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="****" className="w-full px-3 py-3 text-center text-xl tracking-[0.5em] border border-zinc-200 font-mono focus:outline-none focus:border-zinc-400"
                  data-testid="pin-confirm-input" />
              </div>
              {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="error-msg">{error}</p>}
              <button onClick={handleCreatePin} disabled={actionLoading}
                className={`w-full py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${
                  isEntrada ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-600 text-white hover:bg-rose-700'
                }`} data-testid="create-pin-btn">
                {actionLoading ? 'Creando...' : 'Crear PIN y continuar'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Enter PIN */}
        {step === STEPS.ENTER_PIN && selectedWorker && (
          <div className="space-y-4" data-testid="step-enter-pin">
            <button onClick={() => setStep(STEPS.SELECT_WORKER)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800" data-testid="back-btn">
              <ArrowLeft size={14} /> Volver
            </button>
            <div className="bg-white border border-zinc-200 p-5 space-y-4">
              <div className="text-center">
                <div className={`w-14 h-14 mx-auto flex items-center justify-center mb-2 rounded-full border-2 ${
                  isEntrada ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
                }`}>
                  {isEntrada ? <SignIn size={26} className="text-emerald-600" weight="bold" /> : <SignOut size={26} className="text-rose-600" weight="bold" />}
                </div>
                <h2 className="text-base font-semibold text-zinc-900">{selectedWorker.name}</h2>
                <span className="font-mono text-xs px-2 py-0.5 bg-zinc-200 text-zinc-700 font-bold inline-block mt-1">{selectedWorker.code}</span>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600 block mb-1">Ingrese su PIN</label>
                <input type="password" inputMode="numeric" maxLength={4} value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="****" className="w-full px-3 py-3 text-center text-xl tracking-[0.5em] border border-zinc-200 font-mono focus:outline-none focus:border-zinc-400"
                  data-testid="pin-input" autoFocus />
              </div>
              {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="error-msg">{error}</p>}
              <button onClick={handleVerifyThenPhoto} disabled={actionLoading}
                className={`w-full py-3 text-sm font-semibold transition-colors disabled:opacity-50 ${
                  isEntrada ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-600 text-white hover:bg-rose-700'
                }`} data-testid="verify-btn">
                Continuar
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Photo + Location */}
        {step === STEPS.PHOTO && selectedWorker && (
          <div className="space-y-4" data-testid="step-photo">
            <button onClick={() => setStep(selectedWorker.has_pin ? STEPS.ENTER_PIN : STEPS.CREATE_PIN)} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800" data-testid="back-btn">
              <ArrowLeft size={14} /> Volver
            </button>
            <div className="bg-white border border-zinc-200 p-5 space-y-4">
              <div className="text-center">
                <h2 className="text-base font-semibold text-zinc-900 mb-1">Foto de marcacion</h2>
                <p className="text-xs text-zinc-500">{selectedWorker.name} · <span className="font-mono font-bold">{selectedWorker.code}</span></p>
              </div>

              {/* Camera capture */}
              <div className="flex flex-col items-center gap-3">
                {photoData ? (
                  <div className="relative">
                    <img src={photoData} alt="Foto" className="w-48 h-48 object-cover border-2 border-zinc-200 rounded-lg" data-testid="photo-preview" />
                    <button
                      onClick={() => { setPhotoData(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-zinc-900 text-white text-xs flex items-center justify-center rounded-full"
                      data-testid="retake-photo-btn"
                    >
                      X
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-48 h-48 border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors rounded-lg ${
                      isEntrada ? 'border-emerald-300 text-emerald-600 hover:bg-emerald-50' : 'border-rose-300 text-rose-600 hover:bg-rose-50'
                    }`}
                    data-testid="take-photo-btn"
                  >
                    <Camera size={40} weight="light" />
                    <span className="text-xs font-medium">Tomar foto</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  onChange={handleFileCapture}
                  data-testid="photo-input"
                />
              </div>

              {/* Geo status */}
              {branchHasGeo && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 text-xs text-blue-700">
                  <MapPin size={14} weight="bold" className="shrink-0" />
                  <span>Se verificara su ubicacion al confirmar (max {branchData?.geo_radius || 30}m)</span>
                </div>
              )}

              {geoError && <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2">{geoError}</p>}
              {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="error-msg">{error}</p>}

              <button
                onClick={handleSubmitMark}
                disabled={actionLoading || !photoData}
                className={`w-full py-3 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isEntrada ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
                data-testid="confirm-mark-btn"
              >
                {actionLoading ? (geoLoading ? 'Obteniendo ubicacion...' : 'Registrando...') : `Confirmar ${isEntrada ? 'Entrada' : 'Salida'}`}
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Success */}
        {step === STEPS.SUCCESS && (
          <div className="space-y-4 pt-4" data-testid="step-success">
            <div className="bg-white border border-zinc-200 p-6 text-center space-y-4">
              <div className={`w-20 h-20 mx-auto flex items-center justify-center rounded-full border-2 ${
                isEntrada ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
              }`}>
                <CheckCircle size={40} className={isEntrada ? 'text-emerald-600' : 'text-rose-600'} weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900">{successMsg}</h2>
                <p className="text-sm text-zinc-500 mt-1">{selectedWorker?.name}</p>
                {successHora && <p className="font-mono text-xs text-zinc-400 mt-1">{successHora.slice(0, 5)}</p>}
                {successDistance != null && (
                  <p className="text-[10px] text-zinc-400 mt-1 flex items-center justify-center gap-1">
                    <MapPin size={10} /> {successDistance}m del local
                  </p>
                )}
              </div>
              {photoData && (
                <img src={photoData} alt="Foto" className="w-24 h-24 object-cover mx-auto border border-zinc-200 rounded-lg" />
              )}
              <button onClick={handleReset}
                className="w-full py-3 text-sm font-semibold border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors"
                data-testid="new-mark-btn">
                Nueva marcacion
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="text-center py-6 text-sm text-zinc-400">Marca tu entrada y salida del local</div>
    </div>
  );
};

export default AttendanceMarkPage;
